/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Ads Schema", description = "Schema information and diagrams for Hightouch's Campaign Intelligence for ads.", lastUpdatedDate = new Date(1733184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Ads Schema",
        description: "Schema information and diagrams for Hightouch's Campaign Intelligence for ads.",
        lastUpdatedDate: "Tue Dec 03 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Schema with only campaign data for ads",
          "depth": 0,
          "id": "schema-with-only-campaign-data-for-ads"
        }, {
          "value": "Schema with ad, ad group, and campaign data",
          "depth": 0,
          "id": "schema-with-ad-ad-group-and-campaign-data"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      ol: "ol",
      li: "li",
      strong: "strong",
      code: "code",
      img: "img",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Campaign Intelligence is available as an add-on on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Campaign Intelligence for ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/campaign-intelligence/ads",
          children: "ads"
        }), " leverages four types of models:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Users"
          }), " are the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/schema#parent-models",
            children: "parent model"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sessions"
          }), " are a type of ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/events",
            children: _jsx(_components.strong, {
              children: "Event"
            })
          }), " and connect a user to an ad. They necessarily have a timestamp and typically include ", _jsx(_components.code, {
            children: "utm"
          }), " parameters."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ads"
          }), " are a type of ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/assets",
            children: _jsx(_components.strong, {
              children: "Asset"
            })
          }), ", and minimally include campaign and platform information."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ad stats"
          }), " is a model that indicates engagement with an ad, such as clicks, impressions, and spend. It should fit the format of ", _jsx(_components.a, {
            href: "https://fivetran.github.io/dbt_ad_reporting/#!/model/model.ad_reporting.ad_reporting__ad_report",
            children: "this dbt model"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/customer-studio/schema#related-models-and-events",
          children: "relationships"
        }), " between these models depend on whether your ad model has information about the ad itself, such as an ", _jsx(_components.code, {
          children: "ad_name"
        }), " or ", _jsx(_components.code, {
          children: "ad_group_id"
        }), " alongside the ad campaign information or just campaign information."]
      }), "\n", _jsxs(_components.p, {
        children: ["Below are ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Entity%E2%80%93relationship_model",
          children: "entity relationship diagrams"
        }), " (ERDs) that show expected columns and relationships in both situations, including necessary primary (PK) and foreign keys (FK). We note differences between the schemas."]
      }), "\n", _jsx(_components.h2, {
        id: "schema-with-only-campaign-data-for-ads",
        children: "Schema with only campaign data for ads"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/schema-ads-campaign.png",
          alt: "Ads schema with campaign data"
        })
      }), "\n", _jsx(_components.p, {
        children: "The ERD above includes the important features:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Sessions connect to ", _jsx(_components.strong, {
            children: "Ads"
          }), " with a campaign identifier based on what's available in the session, in this case through a mapping from ", _jsx(_components.code, {
            children: "utm_campaign"
          }), " to ", _jsx(_components.code, {
            children: "campaign_name"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ads"
          }), " also connect to ", _jsx(_components.strong, {
            children: "Ad stats"
          }), " through a campaign identifier, in this case the ", _jsx(_components.code, {
            children: "campaign_id"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Hightouch generates a primary key in the ", _jsx(_components.strong, {
            children: "Ad stats"
          }), " model that combines the ", _jsx(_components.code, {
            children: "date_day"
          }), " and ", _jsx(_components.code, {
            children: "campaign_id"
          }), " columns."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "schema-with-ad-ad-group-and-campaign-data",
        children: "Schema with ad, ad group, and campaign data"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/schema-ads-meta.png",
          alt: "Ads schema with additional ads data"
        })
      }), "\n", _jsx(_components.p, {
        children: "Two notable differences in the schema are evident when there is additional ad and ad group information."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Sessions now connect to ", _jsx(_components.strong, {
            children: "Ads"
          }), " through an identifier at the level of an individual ad, in this case with a mapping from ", _jsx(_components.code, {
            children: "utm_content"
          }), " to ", _jsx(_components.code, {
            children: "ad_name"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ads"
          }), " also now connect to ", _jsx(_components.strong, {
            children: "Ad stats"
          }), " through an individual ad level identifier, here the ", _jsx(_components.code, {
            children: "ad_id"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Both ", _jsx(_components.strong, {
            children: "Ads"
          }), " and ", _jsx(_components.strong, {
            children: "Ad stats"
          }), " have additional columns for individual ad level information."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
